import { FC, useMemo } from 'react';
import { Control, FieldValues, useWatch } from 'react-hook-form';
import { Box, useThemeValue } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { convertSizeToNumber } from '@/_shared/helpers';
import { postSurveysCampaignByIdNextCall } from '@/surveys/state/api/postSurveysCampaignByIdNextCall';

type Props = {
  currentStep: ReturnType<(typeof postSurveysCampaignByIdNextCall)['hook']>['activeStep'];
  control: Control<FieldValues>;
};
export const MultipleChoiceAnswer: FC<Props> = ({ currentStep, control }) => {
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 16;
  const values = useWatch({ control });

  const maxSelections = currentStep.nextQuestion?.maxSelections || 1;
  const selectedCount = currentStep.nextQuestion?.options?.reduce((count, option) => {
    const inputId = `multipleChoiceAnswer_${currentStep.nextQuestion?.id || ''}_${option.id}`;
    return values?.[inputId] === true ? count + 1 : count;
  }, 0);

  const isDisabled = (optionId: string) => {
    const inputId = `multipleChoiceAnswer_${currentStep.nextQuestion?.id || ''}_${optionId}`;
    if (values?.[inputId] === true) return false;
    if (selectedCount) {
      return selectedCount >= maxSelections;
    }
    return false;
  };

  const random = 0.5;

  const shuffledOptions = useMemo(() => {
    const options =
      currentStep.nextQuestion?.options?.map((option, index) => ({
        ...option,
        index,
      })) || [];
    return [...options].sort(() => Math.random() - random);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep.nextQuestion?.id]);

  return (
    <Box display='flex' style={{ gap: gapXS }} flexDirection='column'>
      {shuffledOptions.map(option => {
        const inputId = `multipleChoiceAnswer_${currentStep.nextQuestion?.id || ''}_${option.id}`;
        return (
          <Box key={option.id || option.index}>
            <Form.FormCheckbox
              // @ts-ignore
              control={control}
              name={inputId}
              value={option.id}
              background={
                // eslint-disable-next-line no-underscore-dangle
                values?.[inputId] === true ? 'white' : undefined
              }
              iconColor={
                // eslint-disable-next-line no-underscore-dangle
                values?.[inputId] === true ? 'primary' : undefined
              }
              checked={
                // eslint-disable-next-line no-underscore-dangle
                values?.[inputId] === true
              }
              disabled={isDisabled(option.id || '')}
              containerCheckboxProps={{
                px: 's',
                py: 'xs',
                bg: values?.[inputId] === true ? 'primary' : 'grey/dark/background',
                borderRadius: 's',
                display: 'flex',
                flexDirection: 'row',
                style: { cursor: 'pointer' },
              }}
              hasChildrenText={option.text}
            />
          </Box>
        );
      })}
    </Box>
  );
};
